import { comparer, type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import {
  type IConfigureSection,
  type IConfigureSectionModifiedCounts,
  unifiedAgentSectionModifiedLogEvent,
} from 'entities/AIAgent/actions/unifiedAgentEvents'
import type { UnifiedAgentStore } from 'pages/chatbot/ui/UnifiedAgent/store/unifiedAgentStore'

const EMPTY_COUNTS: IConfigureSectionModifiedCounts = {
  trigger: 0,
  instructions: 0,
  knowledgeBase: 0,
  booking: 0,
  autoFollowUp: 0,
}

export class SectionModifiedTrackerStore {
  private _counts: IConfigureSectionModifiedCounts = { ...EMPTY_COUNTS }
  private _sectionBaseline: Partial<Record<IConfigureSection, unknown>> = {}
  private _instructionsSnapshotOnFocus = ''
  private _disposers: IReactionDisposer[] = []

  constructor(private readonly agent: UnifiedAgentStore) {
    makeAutoObservable(this)
  }

  private _sectionSnapshot = (section: IConfigureSection) => {
    switch (section) {
      case 'trigger':
        return [
          this.agent.triggerSectionStore.paramsFilters,
          this.agent.triggerSectionStore.paramsExcludeFilters,
          this.agent.triggerSectionStore.sendFrom,
          this.agent.triggerSectionStore.runAutomatically,
          this.agent.triggerSectionStore.allowReEntering,
        ]
      case 'autoFollowUp':
        return [
          this.agent.autoFollowUpSectionStore.enabled,
          this.agent.autoFollowUpSectionStore.assertiveness,
          this.agent.autoFollowUpSectionStore.followUpWindow,
          this.agent.autoFollowUpSectionStore.sessionLifetime,
        ]
      default:
        return undefined
    }
  }

  private _trackSection = (section: IConfigureSection) =>
    reaction(
      () => this._sectionSnapshot(section),
      (value) => {
        if (comparer.structural(value, this._sectionBaseline[section])) return
        this._sectionBaseline[section] = value
        this.markSectionModified(section)
      },
      { equals: comparer.structural }
    )

  start = () => {
    this._disposers.forEach((dispose) => dispose())
    this._counts = { ...EMPTY_COUNTS }

    this._sectionBaseline.trigger = this._sectionSnapshot('trigger')
    this._sectionBaseline.autoFollowUp = this._sectionSnapshot('autoFollowUp')

    this._disposers = [
      this._trackSection('trigger'),
      this._trackSection('autoFollowUp'),
      reaction(
        () => this.agent.instructionsSectionStore.messageFieldStore.activeMessageField,
        (isFocused, wasFocused) => {
          if (isFocused) {
            this._instructionsSnapshotOnFocus = this.agent.instructionsSectionStore.instructions
            return
          }
          if (!wasFocused) return
          if (
            this.agent.instructionsSectionStore.instructions !== this._instructionsSnapshotOnFocus
          ) {
            this.markSectionModified('instructions')
          }
        }
      ),
    ]
  }

  markSectionModified = (section: IConfigureSection) => {
    this._counts[section]++
  }

  runSectionUpdateSilently = (section: IConfigureSection, update: () => void) => {
    update()
    this._sectionBaseline[section] = this._sectionSnapshot(section)
  }

  fireLogEvent = () => {
    unifiedAgentSectionModifiedLogEvent(this.agent.entities, {
      build_session_id: this.agent.buildSessionId,
      sections: { ...this._counts },
      agent_id: this.agent.id,
    })
    this._counts = { ...EMPTY_COUNTS }
  }

  dispose = () => {
    this._disposers.forEach((dispose) => dispose())
  }
}
